/**
 * WebGL 2 renderer wrapper. Owns the Three.js renderer, scene and global
 * atmosphere. Contains no gameplay logic: it only draws what it is given.
 */
import * as THREE from 'three';
import { GpuTimer } from '../debug/GpuTimer';
import { countResource } from '../debug/LiveResources';

export class RendererInitError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RendererInitError';
  }
}

export type AtmosphereName = 'violet-dusk' | 'dungeon';

/**
 * Atmosphere presets. The scene is unlit (black silhouettes and outlines, see
 * SceneView), so a preset is only the void's colour and how fast the black fog
 * swallows distant outlines.
 */
interface Atmosphere {
  background: number;
  fogDensity: number;
}

const ATMOSPHERES: Readonly<Record<AtmosphereName, Readonly<Atmosphere>>> = {
  // Large open developer levels: a thinner fog, so far platforms still show.
  'violet-dusk': { background: 0x000000, fogDensity: 0.018 },
  // Enclosed halls: the corridor ahead fades to black within a few rooms.
  dungeon: { background: 0x000000, fogDensity: 0.032 },
};

export interface RenderStats {
  drawCalls: number;
  triangles: number;
  geometries: number;
  textures: number;
}

export interface RendererOptions {
  /** MSAA; fixed for the renderer's lifetime. */
  antialias: boolean;
  /** Cap on the device pixel ratio before render scale. */
  maxPixelRatio: number;
}

/** Set after the first successful WebGL 2 check on this page. */
let webgl2Found = false;

export class Renderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene = new THREE.Scene();
  private renderScale = 1;
  private maxPixelRatio: number;
  private contextLost = false;
  /** GPU time per frame, for measurements only (dev builds, `?gputime=1`). */
  readonly gpuTimer: GpuTimer | null = null;
  private disposed = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    options: RendererOptions = { antialias: true, maxPixelRatio: 2 },
  ) {
    this.maxPixelRatio = options.maxPixelRatio;
    // Check WebGL 2 explicitly so the failure message is actionable. Once per page: every probe is one more
    // context, and WebKit counts released ones until they are collected (then drops the oldest, with warnings).
    if (!webgl2Found) {
      const probe = document.createElement('canvas').getContext('webgl2');
      if (!probe) {
        throw new RendererInitError('WebGL 2 is not available in this browser. Enable hardware acceleration or try a current Chrome, Edge or Firefox.');
      }
      probe.getExtension('WEBGL_lose_context')?.loseContext();
      webgl2Found = true;
    }
    try {
      this.renderer = new THREE.WebGLRenderer({ canvas, antialias: options.antialias, powerPreference: 'high-performance' });
    } catch (err: unknown) {
      throw new RendererInitError(`Could not create a WebGL 2 context: ${err instanceof Error ? err.message : String(err)}`);
    }
    countResource('renderers', 1);
    if (import.meta.env.DEV && new URLSearchParams(globalThis.location?.search ?? '').get('gputime') === '1') {
      this.gpuTimer = new GpuTimer(this.renderer.getContext() as WebGL2RenderingContext);
    }
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Unlit outlines: no tone mapping, so white stays white.
    this.renderer.toneMapping = THREE.NoToneMapping;
    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.contextLost = true;
    });
    canvas.addEventListener('webglcontextrestored', () => (this.contextLost = false));

    this.scene.background = new THREE.Color();
    this.scene.fog = new THREE.FogExp2(0, 0);
    this.setAtmosphere('violet-dusk');
  }

  /** Applies a track's atmosphere preset (void colour and fog). */
  setAtmosphere(name: AtmosphereName): void {
    const a = ATMOSPHERES[name];
    (this.scene.background as THREE.Color).set(a.background);
    const fog = this.scene.fog as THREE.FogExp2;
    fog.color.set(a.background);
    fog.density = a.fogDensity;
  }

  get isContextLost(): boolean {
    return this.contextLost;
  }

  get maxAnisotropy(): number {
    return this.renderer.capabilities.getMaxAnisotropy();
  }

  setMaxPixelRatio(ratio: number): void {
    this.maxPixelRatio = Math.max(0.5, Math.min(3, ratio));
    this.resize();
  }

  /** For measurements: what is being drawn with, and on which GPU (when the browser reveals it). */
  describe(): { antialias: boolean; pixelRatio: number; gpu: string } {
    const gl = this.renderer.getContext();
    const info = gl.getExtension('WEBGL_debug_renderer_info');
    const gpu = String(info ? gl.getParameter(info.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    return { antialias: this.antialias, pixelRatio: this.renderer.getPixelRatio(), gpu };
  }

  /** The renderer's antialias setting (fixed at creation). */
  get antialias(): boolean {
    return this.renderer.getContextAttributes()?.antialias ?? false;
  }

  setRenderScale(scale: number): void {
    this.renderScale = Math.max(0.25, Math.min(1, scale));
    this.resize();
  }

  /** Matches the drawing buffer to the canvas' CSS size. Returns the CSS size. */
  resize(): { width: number; height: number } {
    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.maxPixelRatio) * this.renderScale);
    this.renderer.setSize(width, height, false);
    return { width, height };
  }

  render(camera: THREE.Camera): void {
    if (this.contextLost) return;
    this.gpuTimer?.begin();
    this.renderer.render(this.scene, camera);
    this.gpuTimer?.end();
  }

  stats(): RenderStats {
    const info = this.renderer.info;
    return { drawCalls: info.render.calls, triangles: info.render.triangles, geometries: info.memory.geometries, textures: info.memory.textures };
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    countResource('renderers', -1);
    // Release the GPU context now rather than when the canvas is collected (editor playtests make many).
    if (!this.renderer.getContext().isContextLost()) this.renderer.forceContextLoss();
    this.renderer.dispose();
  }
}
